import { ColumnDetails, ColumnType } from '../../../../../../../grid/types/common';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { html, TemplateResult } from 'lit';
import {
  getAdvancedDirectionOptionsForGrid,
  getAdvancedNetworkOptionsForGrid,
  getAdvancedPermissionTypeOptionsForGrid,
  getAdvancedProtocolOptionsForGrid,
  getAdvancedRuleStatusOptionsForGrid,
  getAdvancedRuleTypeOptionsForGrid,
} from '../../../../utils/uiSettings/firewall/firewall.values';
import { getSanitizedItemWithoutAdditionalFrontendFields } from '../utils/converters';
import { FirewallAdvancedRuleFormValueWithAdditionalFrontendFields } from './GzFirewallAdvancedRulesGridDataProvider';
import { isBrokenRule } from '../utils/validators';

export enum Column {
  PRIORITY = 1,
  NAME,
  STATUS,
  RULE_TYPE,
  NETWORK,
  PROTOCOL,
  PERMISSION,

  // those are not mandatory columns
  LOCAL_IP,
  LOCAL_PORT,
  REMOTE_IP,
  REMOTE_PORT,
  DIRECTION,
  MAC,
}

export enum ColumnKeys {
  PRIORITY = 'defaultPositionInGrid',
  NAME = 'name',
  STATUS = 'status',
  RULE_TYPE = 'ruleType',
  NETWORK = 'network',
  PROTOCOL = 'protocol',
  PERMISSION = 'setPermission',

  // those are not mandatory columns
  LOCAL_IP = 'localIp',
  LOCAL_PORT = 'localPort',
  REMOTE_IP = 'remoteIp',
  REMOTE_PORT = 'remotePort',
  DIRECTION = 'direction',
  MAC = 'mac',
}

export const GRID_COLUMNS = [
  Column.PRIORITY,
  Column.NAME,
  Column.STATUS,
  Column.RULE_TYPE,
  Column.NETWORK,
  Column.PROTOCOL,
  Column.PERMISSION,
];

export const FILTERS = [
  { [ColumnKeys.NAME]: { contains: '' } },
  { [ColumnKeys.STATUS]: { in: [] } },
  { [ColumnKeys.RULE_TYPE]: { in: [] } },
  { [ColumnKeys.NETWORK]: { in: [] } },
  { [ColumnKeys.PROTOCOL]: { in: [] } },
  { [ColumnKeys.PERMISSION]: { in: [] } },
];
export const PREDEFINED_FILTERS_KEYS = [
  ColumnKeys.NAME,
  ColumnKeys.STATUS,
  ColumnKeys.RULE_TYPE,
  ColumnKeys.NETWORK,
  ColumnKeys.PROTOCOL,
  ColumnKeys.PERMISSION,
];

export function getGridColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
  return [
    {
      column: Column.PRIORITY,
      key: ColumnKeys.PRIORITY,
      columnName: translateFn('Priority'),
      type: ColumnType.NUMBER,
      removable: false,
      filteringDisabled: true,
    },
    {
      column: Column.NAME,
      key: ColumnKeys.NAME,
      columnName: translateFn('Rule name'),
      type: ColumnType.STRING,
      removable: false,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.STATUS,
      key: ColumnKeys.STATUS,
      columnName: translateFn('Status'),
      values: getAdvancedRuleStatusOptionsForGrid(),
      type: ColumnType.ENUM,
      removable: false,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.RULE_TYPE,
      key: ColumnKeys.RULE_TYPE,
      columnName: translateFn('Rule type'),
      values: getAdvancedRuleTypeOptionsForGrid(),
      type: ColumnType.ENUM,
      removable: false,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.NETWORK,
      key: ColumnKeys.NETWORK,
      columnName: translateFn('Network'),
      values: getAdvancedNetworkOptionsForGrid(),
      type: ColumnType.ENUM,
      removable: false,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.PROTOCOL,
      key: ColumnKeys.PROTOCOL,
      columnName: translateFn('Protocol'),
      values: getAdvancedProtocolOptionsForGrid(),
      type: ColumnType.ENUM,
      removable: false,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.PERMISSION,
      key: ColumnKeys.PERMISSION,
      columnName: translateFn('Permission'),
      values: getAdvancedPermissionTypeOptionsForGrid(),
      type: ColumnType.ENUM,
      removable: false,
      sortable: false,
      editingDisabled: true,
    },

    // those are not mandatory columns
    {
      column: Column.LOCAL_IP,
      key: ColumnKeys.LOCAL_IP,
      columnName: translateFn('Local IP'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.LOCAL_PORT,
      key: ColumnKeys.LOCAL_PORT,
      columnName: translateFn('Local port'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.REMOTE_IP,
      key: ColumnKeys.REMOTE_IP,
      columnName: translateFn('Remote IP'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.REMOTE_PORT,
      key: ColumnKeys.REMOTE_PORT,
      columnName: translateFn('Remote port'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.DIRECTION,
      key: ColumnKeys.DIRECTION,
      columnName: translateFn('Direction'),
      values: getAdvancedDirectionOptionsForGrid(),
      type: ColumnType.ENUM,
      removable: true,
      sortable: false,
      editingDisabled: true,
    },
    {
      column: Column.MAC,
      key: ColumnKeys.MAC,
      columnName: translateFn('MAC'),
      type: ColumnType.STRING,
      removable: true,
      sortable: false,
      editingDisabled: true,
    },
  ];
}

export function nameCellCustomRenderer(
  dispatchClickEventFn: (item) => void,
  rowData: unknown & { item: FirewallAdvancedRuleFormValueWithAdditionalFrontendFields }
): TemplateResult {

  if (isBrokenRule(rowData.item)) {
    const tooltipText = translateFn('This rule has been disabled due to a missing application path. Add the required application path and enable it.');
    return html`
      <gz-tooltip tooltipText="${tooltipText}" class="cell-label">
        <gz-bullet>
            <span
              class="accent cell-label"
              style="cursor: pointer;"
              @click="${() => dispatchClickEventFn(getSanitizedItemWithoutAdditionalFrontendFields(rowData.item))}"
            >
              ${rowData.item.details.name}
            </span>
        </gz-bullet>
      </gz-tooltip>`;
  }

  return html`
    <span
      class="accent cell-label"
      style="cursor: pointer;"
      @click="${() => dispatchClickEventFn(getSanitizedItemWithoutAdditionalFrontendFields(rowData.item))}"
    >
      ${rowData.item.details.name}
    </span>`;
}

export function networkCellCustomRenderer(rowData: unknown & { item: any }): TemplateResult {
  const columnValues = getAdvancedNetworkOptionsForGrid();
  const label = rowData.item.network
    .map(val => {
      const columnValue = columnValues.find(column => column.value === val);
      return columnValue ? columnValue.label : val;
    })
    .join(', ');

  return html`<gz-grid-cell-default-renderer .label="${label}"> </gz-grid-cell-default-renderer>`;
}
